import { ReactElement } from "react";
import Timer from "react-compound-timer";
import { Container } from "@remo-co/ui-core/src/components/Container";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { useI18n } from "i18n";

type TimerUnitProps = {
  Component:
    | typeof Timer.Days
    | typeof Timer.Hours
    | typeof Timer.Minutes
    | typeof Timer.Seconds;
  unit: string;
};

const TimerUnit = ({ Component, unit }: TimerUnitProps) => (
  <Container flex column alignItems="center" fullWidth={false}>
    <Typography variant="caption">{unit}</Typography>
    <Typography variant="h3" data-testid={unit}>
      <Component formatValue={(value: number) => `${value}`.padStart(2, "0")} />
    </Typography>
  </Container>
);

const Separator = () => (
  <Container flex fullWidth={false} justifyContent="center" alignItems="center">
    <Typography variant="h3">:</Typography>
  </Container>
);

type CountdownTimerProps = {
  startTime: number;
  endTime: number;
  onEnd?: () => void;
};

const CountDownTimer = ({
  startTime,
  endTime,
  onEnd,
}: CountdownTimerProps): ReactElement => {
  const { t } = useI18n();
  const delta = Math.max(endTime - startTime, 0);

  return (
    <Timer
      initialTime={delta}
      direction="backward"
      checkpoints={[{ time: 0, callback: () => onEnd?.() }]}
    >
      <Container
        flex
        alignItems="flex-end"
        justifyContent="space-between"
        fullWidth={false}
      >
        <TimerUnit data-testid="days" Component={Timer.Days} unit={t("days")} />
        <Separator />
        <TimerUnit
          data-testid="hours"
          Component={Timer.Hours}
          unit={t("hours")}
        />
        <Separator />
        <TimerUnit
          data-testid="minutes"
          Component={Timer.Minutes}
          unit={t("minutes")}
        />
        <Separator />
        <TimerUnit
          data-testid="seconds"
          Component={Timer.Seconds}
          unit={t("seconds")}
        />
      </Container>
    </Timer>
  );
};

export default CountDownTimer;
